import { Libraries, useLoadScript } from '@react-google-maps/api';
import { COMMONTYPES } from 'TYPES/event.types';
import { DropDownIcon, LocationIcon } from 'assets/Icons';
import QUERY_KEYS from 'network/config/queryKeys';
import { addAddress, getAllAddress, setDefaultAddress } from 'network/services/address';
import { validateAddressDeliveryCallback } from 'network/services/order';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import userAddressAtom from 'store/atoms/address';
import { profileAtom } from 'store/atoms/profileAtom';
import { userLocationAtom } from 'store/atoms/userLocationAtom';
import { setCacheAddress } from 'utils/address';
import { truncateCharCount } from 'utils/truncate';
import { InputField, Spacer } from '../../atoms';
import { DropDown } from '../DropDown/DropDown';
import { CurrentLocationButton, LocationIconWrapper, LocationText } from './addressDropdownStyles';
import { AddressCon, DropListCON, Spinner } from './styles';

// Add custom styles for Google Places Autocomplete
const AutocompleteStyles = `
  .pac-container {
    z-index: 99999 !important;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    margin-top: 2px;
    font-family: inherit;
    max-width: none !important;
  }
  .pac-item {
    padding: 12px 16px;
    cursor: pointer;
    font-size: 14px;
    border-bottom: 1px solid #f0f0f0;
  }
  .pac-item:hover {
    background-color: #f8f9fa;
  }
  .pac-item-selected {
    background-color: #e3f2fd;
  }
  .pac-item:last-child {
    border-bottom: none;
  }
  .pac-matched {
    font-weight: 600;
    color: #1976d2;
  }
`;

// Google Maps libraries to load
const libraries: Libraries = ['places'];

export const AddressDropDown = () => {
  const [search, setSearch] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [hasSetInitialAddress, setHasSetInitialAddress] = useState(false);

  const { data, isFetching } = useQuery([QUERY_KEYS.CUSTOMER_ADDRESSES], getAllAddress);
  const [userAddress, setUserAddress] = useRecoilState(userAddressAtom);
  const setUserLocation = useSetRecoilState(userLocationAtom);
  const profile = useRecoilValue(profileAtom);
  const locationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  // Load Google Maps API using the same method as EditAddressModal
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries
  });

  // Show error message if the API fails to load
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
    }
  }, [loadError]);

  // Memoize addresses to prevent unnecessary recalculations
  const addresses = useMemo(() => (data?.data ? data.data : []), [data?.data]);

  // Memoize filtered addresses
  const filtered = useMemo(() => {
    if (search.length <= 2) return addresses;
    const searchLower = search.toLowerCase();
    return addresses.filter(({ street }) => street.toLowerCase().includes(searchLower));
  }, [addresses, search]);

  const { mutateAsync: validateDeliveryAddress } = useMutation({
    mutationFn: validateAddressDeliveryCallback
  });

  const { mutateAsync: addNewAddress } = useMutation({
    mutationFn: addAddress
  });

  // Memoize saveLocationAsAddress to prevent recreating getCurrentLocation
  const saveLocationAsAddress = useCallback(
    async (
      formattedAddress: string,
      state: string,
      city: string,
      country: string,
      zipCode: string,
      latitude: number,
      longitude: number
    ) => {
      try {
        setIsSavingAddress(true);

        // Check if the street already exists in current addresses
        const existingAddress = addresses.find(
          address => address.street.toLowerCase().trim() === formattedAddress.toLowerCase().trim()
        );

        if (existingAddress) {
          // If address already exists, set it as default instead of creating a new one
          await setDefaultAddress(existingAddress.id.toString());
          queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);

          // Update local state and cache
          setUserAddress(existingAddress.street);
          setCacheAddress(existingAddress.street);

          return existingAddress;
        }

        // Format the phone number from profile or use a placeholder
        let phoneNumber = profile?.phoneNumber || '';
        if (!phoneNumber) {
          phoneNumber = '+2340000000000'; // Default placeholder
        }

        // Format name from profile
        const name =
          profile?.customer_type === 'BUSINESS'
            ? profile?.business_name + ' Business'
            : (profile?.firstname || 'User') + ' ' + (profile?.lastname || 'User');

        // Validate the address with delivery service
        const validationResult = await validateDeliveryAddress({
          name: name,
          email: profile?.email || 'user@example.com',
          phone: phoneNumber,
          address: `${formattedAddress}, ${state}, ${country}`
        });

        if (validationResult.status !== 'success') {
          throw new Error('Address validation failed');
        }

        // Add the new address
        const newAddressResult = await addNewAddress({
          status: true, // Set as default
          street: formattedAddress,
          state: state || 'Lagos',
          country: country || 'Nigeria',
          city: city || '',
          longitude: longitude,
          latitude: latitude,
          address_code: validationResult?.data?.address_code || '',
          phoneNumber: validationResult?.data?.phone || phoneNumber
        });

        // Refresh the address list
        queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);

        // Update local state and cache
        setUserAddress(formattedAddress);
        setCacheAddress(formattedAddress);

        return newAddressResult;
      } catch (error) {
        console.error('Error saving address:', error);
        enqueueSnackbar('Failed to save address. Please try again.', {
          variant: 'error'
        });
        throw error;
      } finally {
        setIsSavingAddress(false);
      }
    },
    [profile, validateDeliveryAddress, addNewAddress, queryClient, addresses, setUserAddress]
  );

  // Initialize Google Places Autocomplete (matches EditAddressModal pattern)
  useEffect(() => {
    // Add custom styles for Google Places Autocomplete
    const styleEl = document.createElement('style');
    styleEl.type = 'text/css';
    styleEl.appendChild(document.createTextNode(AutocompleteStyles));
    document.head.appendChild(styleEl);

    if (isLoaded && searchInputRef.current && !autocompleteRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(searchInputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'ng' }, // Restrict to Nigeria
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        });

        // Listen for place selection
        autocompleteRef.current?.addListener('place_changed', async () => {
          const place = autocompleteRef.current?.getPlace();

          if (place && place.geometry && place.geometry.location) {
            const formattedAddress = place.formatted_address || '';

            // Extract address components
            let state = '';
            let city = '';
            let country = '';
            let zipCode = '';

            if (place.address_components) {
              place.address_components.forEach((component: any) => {
                if (component.types.includes('administrative_area_level_1')) {
                  state = component.long_name;
                } else if (component.types.includes('locality')) {
                  city = component.long_name;
                } else if (component.types.includes('country')) {
                  country = component.long_name;
                } else if (component.types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              });
            }

            // Get coordinates
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            // Clear search input
            setSearch('');

            // Update location state
            setUserLocation({
              lat: latitude.toString(),
              display_name: formattedAddress,
              name: formattedAddress,
              place_id: place.place_id || '0',
              lon: longitude.toString(),
              address: {
                state: state || 'Lagos',
                city: city || '',
                country: country || 'Nigeria',
                postcode: zipCode || ''
              }
            });

            // Save the address automatically
            try {
              await saveLocationAsAddress(
                formattedAddress,
                state || 'Lagos',
                city || '',
                country || 'Nigeria',
                zipCode || '',
                latitude,
                longitude
              );

              enqueueSnackbar('Address added and set as default!', { variant: 'success' });
            } catch (error) {
              console.error('Error saving autocomplete address:', error);
            }
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
        enqueueSnackbar('Error initializing address autocomplete', { variant: 'error' });
      }
    }

    return () => {
      // Clean up styles when component unmounts
      if (styleEl && styleEl.parentNode) {
        styleEl.parentNode.removeChild(styleEl);
      }
    };
  }, [isLoaded, saveLocationAsAddress, setUserLocation]);

  React.useEffect(() => {
    if (addresses.length > 0 && !hasSetInitialAddress && (!userAddress || userAddress === '')) {
      const defaultAddress = addresses.find(item => item.status === true) || addresses[0];
      if (defaultAddress) {
        setCacheAddress(defaultAddress.street);
        setUserAddress(defaultAddress.street);
        setHasSetInitialAddress(true);
      }
    }
  }, [addresses, hasSetInitialAddress, userAddress, setUserAddress]);

  React.useEffect(() => {
    return () => {
      if (locationTimeoutRef.current) {
        clearTimeout(locationTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = useCallback((e: COMMONTYPES['inputChange']) => {
    e.preventDefault();
    setSearch(e.target.value);
  }, []);

  // Memoize getCurrentLocation with proper dependencies
  const getCurrentLocation = useCallback(() => {
    // Prevent multiple clicks
    if (isLocating) return;

    // Clear any existing timeout
    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
    }

    if (!navigator.geolocation) {
      enqueueSnackbar('Geolocation is not supported by your browser', { variant: 'error' });
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async position => {
        try {
          const { latitude, longitude } = position.coords;

          // Use Google Maps Geocoding API to get address from coordinates
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
          );

          const data = await response.json();

          if (data.status === 'OK' && data.results.length > 0) {
            const addressResult = data.results[0];
            const formattedAddress = addressResult.formatted_address;

            // Extract components
            let state = '';
            let city = '';
            let country = '';
            let zipCode = '';

            addressResult.address_components.forEach((component: any) => {
              if (component.types.includes('administrative_area_level_1')) {
                state = component.long_name;
              } else if (component.types.includes('locality')) {
                city = component.long_name;
              } else if (component.types.includes('country')) {
                country = component.long_name;
              } else if (component.types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            });

            // Update UI with the address
            setUserAddress(formattedAddress);
            setCacheAddress(formattedAddress);

            setUserLocation({
              lat: latitude.toString(),
              display_name: formattedAddress,
              name: formattedAddress,
              place_id: '0',
              lon: longitude.toString(),
              address: {
                state: state,
                city: city,
                country: country,
                postcode: zipCode
              }
            });

            // Automatically save the address and make it default
            try {
              await saveLocationAsAddress(formattedAddress, state, city, country, zipCode, latitude, longitude);
            } catch (saveError) {
              console.error('Error during address save:', saveError);
              // Error is already handled in saveLocationAsAddress function
            }
          } else {
            throw new Error(data.status || 'Could not retrieve address from coordinates');
          }
        } catch (error) {
          console.error('Error getting location:', error);
          enqueueSnackbar(`Failed to get your current location: ${(error as any).message || 'Unknown error'}`, {
            variant: 'error'
          });
        } finally {
          // Add a small delay before allowing another location request
          locationTimeoutRef.current = setTimeout(() => {
            setIsLocating(false);
          }, 1000);
        }
      },
      error => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Could not get your current location';

        if (error.code === 1) {
          errorMessage = 'Location access denied. Please enable location services in your browser.';
        } else if (error.code === 2) {
          errorMessage = 'Location unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });

        // Add a small delay before allowing another location request
        locationTimeoutRef.current = setTimeout(() => {
          setIsLocating(false);
        }, 1000);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [isLocating, setUserAddress, setUserLocation, saveLocationAsAddress]);

  const saveAsDefault = useCallback(
    async (val: string, bool: boolean) => {
      if (bool) return;

      try {
        await setDefaultAddress(val);
        queryClient.invalidateQueries(QUERY_KEYS.CUSTOMER_ADDRESSES);

        const selectedAddress = addresses.find(address => address.id === Number(val));
        if (selectedAddress) {
          // Update local state and cache
          setUserAddress(selectedAddress.street);
          setCacheAddress(selectedAddress.street);
          enqueueSnackbar('Address Updated Successfully', {
            variant: 'success'
          });
        }
      } catch (error) {
        console.error('Error setting default address:', error);
      } finally {
        setSearch(''); // Clear search after setting default
      }
    },
    [addresses, queryClient, setUserAddress]
  );

  return (
    <DropDown
      x="right"
      label={
        <h3>
          <LocationIcon />
          <Spacer width={10} />
          <span>{truncateCharCount(userAddress !== '' ? userAddress : 'No Default Address', 18)}</span>
          <Spacer width={10} />
          <DropDownIcon />
        </h3>
      }
      margin="10px 0 0 0">
      <AddressCon onClick={e => e.stopPropagation()}>
        <div className="heading">Delivering to</div>
        <Spacer height={10} />
        <InputField
          type="text"
          name="search"
          onChange={handleSearch}
          value={search}
          disabled={isFetching}
          placeholder={isLoaded ? 'Search or type address...' : 'Search addresses...'}
          inputRef={searchInputRef}
          autoComplete="off"
          onFocus={() => {
            // Reset any previous errors when user focuses on the input
            if (autocompleteRef.current) {
              // Clear any previous place data
              autocompleteRef.current.set('place', null);
            }
          }}
        />
        {isSavingAddress && (
          <div
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
            Saving address...
          </div>
        )}
        <DropListCON>
          {!isFetching ? (
            <div className="list-con">
              {filtered.length ? (
                filtered.map(({ street, state, country, status, id }) => (
                  <div className="list" onClick={() => saveAsDefault(`${id}`, status)} data-current={status} key={id}>
                    <div className="name">{street}</div>
                    <div className="desc">
                      {state} {country}
                    </div>
                  </div>
                ))
              ) : (
                <div className="loading">
                  {search.length > 2 ? 'No address found' : 'Start typing to search addresses...'}
                </div>
              )}
            </div>
          ) : (
            <div className="loading">
              <Spinner size={30} />
            </div>
          )}
        </DropListCON>
        <CurrentLocationButton
          onClick={getCurrentLocation}
          disabled={isLocating || isSavingAddress}
          style={{
            background: '#1976d2', // blue
            color: '#fff',         // white text
            border: 'none'
          }}
        >
          <LocationIconWrapper $isLoading={isLocating || isSavingAddress}>
            <LocationIcon />
          </LocationIconWrapper>
          <LocationText>
            {isLocating ? (
              <>
                Locating
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </>
            ) : isSavingAddress ? (
              <>
                Saving Address
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </>
            ) : (
              'Use My Current Location'
            )}
          </LocationText>
        </CurrentLocationButton>
      </AddressCon>
    </DropDown>
  );
};
